import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useHotel } from '@/context/HotelContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, BedDouble, Trash2, Pencil, User, RefreshCw } from 'lucide-react';
import { getErrorMessage } from '@/lib/errorUtils';

import { API } from '@/config/api';
const AUTO_REFRESH_INTERVAL = 15000; // 15 seconds

// All possible room statuses
const ALL_ROOM_STATUSES = [
  { id: 'available', label: 'Available' },
  { id: 'occupied', label: 'Occupied' },
  { id: 'dirty', label: 'Dirty' },
  { id: 'clean', label: 'Clean' },
  { id: 'out_of_service', label: 'Out of Service' },
  { id: 'maintenance', label: 'Maintenance' },
];

const RoomsPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { rooms, roomTypes, setRooms, setRoomTypes, fetchRooms, fetchRoomTypes, hotelSettings } = useHotel();
  const [showRoomTypeDialog, setShowRoomTypeDialog] = useState(false);
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingRoomType, setEditingRoomType] = useState(null);
  const [editingRoom, setEditingRoom] = useState(null);
  const [rtForm, setRtForm] = useState({ name: '', description: '', base_price: '', max_occupancy: 2 });
  const [roomForm, setRoomForm] = useState({ room_number: '', floor: 1, room_type_id: '' });
  const [occupiedRoomGuests, setOccupiedRoomGuests] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  // Fetch current guests for occupied rooms
  const fetchOccupiedRoomGuests = useCallback(async () => {
    if (!user?.hotel_id) return;
    try {
      const headers = getAuthHeaders();
      const resResponse = await axios.get(`${API}/hotels/${user.hotel_id}/reservations?status=checked_in`, { headers });
      const checkedInReservations = resResponse.data || [];
      
      const guestsResponse = await axios.get(`${API}/hotels/${user.hotel_id}/guests`, { headers });
      const guests = guestsResponse.data || [];
      
      const roomGuestMap = {};
      checkedInReservations.forEach(res => {
        const guest = guests.find(g => g.id === res.guest_id);
        if (guest) {
          roomGuestMap[res.room_id] = {
            name: `${guest.first_name} ${guest.last_name}`,
            checkOutDate: res.check_out_date
          };
        }
      });
      setOccupiedRoomGuests(roomGuestMap);
    } catch (error) {
      console.error('Failed to fetch occupied room guests:', error);
    }
  }, [user?.hotel_id, getAuthHeaders]);

  // Refresh all data
  const refreshData = useCallback(async (showIndicator = false) => {
    if (showIndicator) setRefreshing(true);
    await Promise.all([fetchRooms(), fetchRoomTypes(), fetchOccupiedRoomGuests()]);
    if (showIndicator) setRefreshing(false);
  }, [fetchRooms, fetchRoomTypes, fetchOccupiedRoomGuests]);

  // Initial fetch
  useEffect(() => {
    fetchOccupiedRoomGuests();
  }, [fetchOccupiedRoomGuests]);

  // Auto-refresh
  useEffect(() => {
    const intervalId = setInterval(() => refreshData(false), AUTO_REFRESH_INTERVAL);
    return () => clearInterval(intervalId);
  }, [refreshData]);

  const resetRtForm = () => {
    setRtForm({ name: '', description: '', base_price: '', max_occupancy: 2 });
    setEditingRoomType(null);
  };

  async function createOrUpdateRoomType(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        name: rtForm.name,
        description: rtForm.description,
        base_rate: parseFloat(rtForm.base_price),
        max_occupancy: rtForm.max_occupancy,
        extra_bed_price: 0,
        amenities: []
      };

      if (editingRoomType) {
        await axios.put(`${API}/hotels/${user.hotel_id}/room-types/${editingRoomType.id}`, payload, { headers: getAuthHeaders() });
        setRoomTypes(roomTypes.map(rt => rt.id === editingRoomType.id ? { ...rt, ...payload } : rt));
        toast.success('Room type updated');
      } else {
        const res = await axios.post(`${API}/hotels/${user.hotel_id}/room-types`, payload, { headers: getAuthHeaders() });
        setRoomTypes([...roomTypes, res.data]);
        toast.success('Room type created');
      }
      setShowRoomTypeDialog(false);
      resetRtForm();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save room type'));
    }
    setLoading(false);
  }

  async function deleteRoomType(rtId) {
    const roomsUsingType = rooms.filter(r => r.room_type_id === rtId);
    if (roomsUsingType.length > 0) {
      toast.error(`Cannot delete: ${roomsUsingType.length} room(s) use this type`);
      return;
    }
    if (!window.confirm('Delete this room type?')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/room-types/${rtId}`, { headers: getAuthHeaders() });
      setRoomTypes(roomTypes.filter(rt => rt.id !== rtId));
      toast.success('Room type deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete room type'));
    }
  }

  function openEditRoomType(rt) {
    setEditingRoomType(rt);
    setRtForm({
      name: rt.name,
      description: rt.description || '',
      base_price: (rt.base_rate || rt.base_price || 0).toString(),
      max_occupancy: rt.max_occupancy
    });
    setShowRoomTypeDialog(true);
  }

  async function createOrUpdateRoom(e) {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { 
        number: roomForm.room_number, 
        floor: parseInt(roomForm.floor), 
        room_type_id: roomForm.room_type_id 
      };

      if (editingRoom) {
        await axios.put(`${API}/hotels/${user.hotel_id}/rooms/${editingRoom.id}`, payload, { headers: getAuthHeaders() });
        setRooms(rooms.map(r => r.id === editingRoom.id ? { ...r, ...payload } : r));
        toast.success('Room updated');
      } else {
        const res = await axios.post(`${API}/hotels/${user.hotel_id}/rooms`, payload, { headers: getAuthHeaders() });
        setRooms([...rooms, res.data]);
        toast.success('Room created');
      }
      setShowRoomDialog(false);
      resetRoomForm();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save room'));
    }
    setLoading(false);
  }

  function resetRoomForm() {
    setRoomForm({ room_number: '', floor: 1, room_type_id: '' });
    setEditingRoom(null);
  }

  function openEditRoom(room) {
    setEditingRoom(room);
    setRoomForm({
      room_number: room.number,
      floor: room.floor,
      room_type_id: room.room_type_id
    });
    setShowRoomDialog(true);
  }

  async function updateStatus(roomId, status) {
    try {
      await axios.put(`${API}/hotels/${user.hotel_id}/rooms/${roomId}`, { status }, { headers: getAuthHeaders() });
      setRooms(rooms.map(r => r.id === roomId ? { ...r, status } : r));
      toast.success('Status updated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
    }
  }

  async function deleteRoom(roomId) {
    if (!window.confirm('Delete this room?')) return;
    try {
      await axios.delete(`${API}/hotels/${user.hotel_id}/rooms/${roomId}`, { headers: getAuthHeaders() });
      setRooms(rooms.filter(r => r.id !== roomId));
      toast.success('Room deleted');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to delete room'));
    }
  }

  function getTypeName(id) {
    const found = roomTypes.find(r => r.id === id);
    return found ? found.name : 'Unknown';
  }

  function getStatusClass(status) {
    if (status === 'available') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400';
    if (status === 'occupied') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400';
    if (status === 'dirty') return 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400';
    if (status === 'clean') return 'bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400';
    if (status === 'maintenance') return 'bg-orange-100 text-orange-700 dark:bg-orange-900/50 dark:text-orange-400';
    return 'bg-rose-100 text-rose-700 dark:bg-rose-900/50 dark:text-rose-400';
  }

  // Get enabled room statuses from settings (default all enabled)
  const enabledStatuses = ALL_ROOM_STATUSES.filter(status => {
    const roomStatuses = hotelSettings?.room_statuses;
    if (!roomStatuses) return true; // If no settings, show all
    return roomStatuses[status.id] !== false;
  });

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="rooms-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-xl sm:text-2xl font-bold font-['Outfit']">Rooms Management</h1>
        <Button variant="outline" size="sm" onClick={() => refreshData(true)} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="hidden sm:inline">{refreshing ? 'Refreshing...' : 'Refresh'}</span>
        </Button>
      </div>

      <Tabs defaultValue="rooms" className="space-y-4">
        <TabsList className="w-full sm:w-auto">
          <TabsTrigger value="rooms" data-testid="rooms-tab" className="flex-1 sm:flex-none">Rooms</TabsTrigger>
          <TabsTrigger value="room-types" data-testid="room-types-tab" className="flex-1 sm:flex-none">Types</TabsTrigger>
        </TabsList>

        <TabsContent value="rooms" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showRoomDialog} onOpenChange={(open) => { setShowRoomDialog(open); if (!open) resetRoomForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-room-btn" disabled={roomTypes.length === 0}>
                  <Plus className="h-4 w-4 mr-2" />Add Room
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingRoom ? 'Edit Room' : 'Add Room'}</DialogTitle></DialogHeader>
                <form onSubmit={createOrUpdateRoom} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Room Number</Label>
                    <Input value={roomForm.room_number} onChange={e => setRoomForm({ ...roomForm, room_number: e.target.value })} data-testid="room-number-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Floor</Label>
                    <Input type="number" value={roomForm.floor} onChange={e => setRoomForm({ ...roomForm, floor: e.target.value })} data-testid="room-floor-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Room Type</Label>
                    <Select value={roomForm.room_type_id} onValueChange={v => setRoomForm({ ...roomForm, room_type_id: v })}>
                      <SelectTrigger data-testid="room-type-select"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent>
                        {roomTypes.map(rt => <SelectItem key={rt.id} value={rt.id}>{rt.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-room-btn">{loading ? (editingRoom ? 'Updating...' : 'Creating...') : (editingRoom ? 'Update' : 'Create')}</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {rooms.map(room => {
              const guestInfo = occupiedRoomGuests[room.id];
              return (
                <Card key={room.id} data-testid={`room-card-${room.number}`} className={room.status === 'occupied' ? 'border-blue-300 dark:border-blue-700' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${room.status === 'occupied' ? 'bg-blue-100 dark:bg-blue-950' : 'bg-slate-100 dark:bg-slate-800'}`}>
                          <BedDouble className={`h-5 w-5 ${room.status === 'occupied' ? 'text-blue-600' : 'text-slate-600'}`} />
                        </div>
                        <div>
                          <h3 className="font-semibold">Room {room.number}</h3>
                          <p className="text-sm text-muted-foreground">Floor {room.floor}</p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="icon" onClick={() => openEditRoom(room)} data-testid={`edit-room-${room.number}`}>
                          <Pencil className="h-4 w-4 text-blue-500" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => deleteRoom(room.id)}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                    
                    <p className="text-sm text-muted-foreground mb-3">{getTypeName(room.room_type_id)}</p>
                    
                    {/* Guest Name Display for Occupied Rooms */}
                    {room.status === 'occupied' && guestInfo && (
                      <div className="mb-3 p-2 bg-blue-50 dark:bg-blue-900/30 rounded-md">
                        <div className="flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600" />
                          <span className="text-sm font-medium text-blue-700 dark:text-blue-300">{guestInfo.name}</span>
                        </div>
                        {guestInfo.checkOutDate && (
                          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                            Checkout: {new Date(guestInfo.checkOutDate).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    )}
                    
                    <Select value={room.status} onValueChange={v => updateStatus(room.id, v)}>
                      <SelectTrigger className={getStatusClass(room.status)} data-testid={`room-status-${room.number}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {enabledStatuses.map(status => (
                          <SelectItem key={status.id} value={status.id}>{status.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          {rooms.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground">No rooms yet. Create a room type first.</CardContent></Card>}
        </TabsContent>

        <TabsContent value="room-types" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={showRoomTypeDialog} onOpenChange={(open) => { setShowRoomTypeDialog(open); if (!open) resetRtForm(); }}>
              <DialogTrigger asChild>
                <Button data-testid="add-room-type-btn"><Plus className="h-4 w-4 mr-2" />Add Type</Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>{editingRoomType ? 'Edit Room Type' : 'Add Room Type'}</DialogTitle></DialogHeader>
                <form onSubmit={createOrUpdateRoomType} className="space-y-4">
                  <div className="space-y-2">
                    <Label>Name</Label>
                    <Input value={rtForm.name} onChange={e => setRtForm({ ...rtForm, name: e.target.value })} data-testid="room-type-name-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Input value={rtForm.description} onChange={e => setRtForm({ ...rtForm, description: e.target.value })} data-testid="room-type-description-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Base Price (€)</Label>
                    <Input type="number" step="0.01" value={rtForm.base_price} onChange={e => setRtForm({ ...rtForm, base_price: e.target.value })} data-testid="room-type-price-input" required />
                  </div>
                  <div className="space-y-2">
                    <Label>Max Occupancy</Label>
                    <Input type="number" value={rtForm.max_occupancy} onChange={e => setRtForm({ ...rtForm, max_occupancy: parseInt(e.target.value) })} data-testid="room-type-occupancy-input" />
                  </div>
                  <Button type="submit" className="w-full" disabled={loading} data-testid="submit-room-type-btn">
                    {loading ? 'Saving...' : (editingRoomType ? 'Update' : 'Create')}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {roomTypes.map(rt => (
              <Card key={rt.id} data-testid={`room-type-card-${rt.name}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">{rt.name}</CardTitle>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEditRoomType(rt)} data-testid={`edit-room-type-${rt.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => deleteRoomType(rt.id)} data-testid={`delete-room-type-${rt.id}`}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-2">{rt.description || 'No description'}</p>
                  <div className="flex justify-between mb-1"><span className="text-sm text-muted-foreground">Price</span><span className="font-semibold">€{rt.base_rate || rt.base_price || 0}</span></div>
                  <div className="flex justify-between"><span className="text-sm text-muted-foreground">Max</span><span>{rt.max_occupancy} guests</span></div>
                  <Badge variant="secondary" className="mt-2">{rooms.filter(r => r.room_type_id === rt.id).length} rooms</Badge>
                </CardContent>
              </Card>
            ))}
          </div>
          {roomTypes.length === 0 && <Card><CardContent className="py-8 text-center text-muted-foreground">No room types yet.</CardContent></Card>}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default RoomsPage;
